import { Dispatch } from "react";
import * as OBC from "@thatopen/components";
import * as OBF from "@thatopen/components-front";
import * as BUI from "@thatopen/ui";
import * as THREE from "three";
import type * as FRAGS from "@thatopen/fragments";
import {
  buildGuidToLocalIdMap,
  buildPresentationLayersIndexData,
  extractPresentationLayersFromIfc,
  invalidatePresentationLayersCache,
  layerExpressMapToLocalIds,
  PRESENTATION_LAYERS_CLASSIFICATION,
  readPresentationLayersFromModel,
} from "./presentation-layers";
import {
  countSelectedElements,
  filterPropertySets,
  FormattedPropertySets,
  getElementName,
  getElementPropertySets,
  getFirstSelectedElement,
  sortPropertySetNames,
} from "./property-sets";
import {
  MeasureUnits,
  ViewerNavigation,
  ViewerProjection,
  ViewerTool,
  ViewerTools,
} from "./viewer-tools";

interface LayerRow extends BUI.TableRowData {
  Name: string;
  Actions: string;
  classification: string;
}

interface PropertiesPanelState {
  elementName: string;
  propertySets: FormattedPropertySets;
  searchQuery: string;
  statusMessage: string | null;
  loading: boolean;
}

export interface ModelFragmentsPayload {
  // A presigned Wasabi URL rather than the buffer itself. The fragments used to
  // arrive base64 encoded inside the JSON response, which meant decoding them
  // with a per-byte loop on the main thread before anything could render.
  modelFragmentsUrl: string;
  modelName?: string;
}

const IFC_WASM = {
  path: "https://unpkg.com/web-ifc@0.0.77/",
  absolute: true,
};

export class StandardScene {
  private components: OBC.Components;
  private worlds: OBC.Worlds;
  private world: OBC.World;
  private camera: OBC.OrthoPerspectiveCamera;
  private scene: THREE.Scene;
  private fragments: OBC.FragmentsManager;
  private ifcLoader!: OBC.IfcLoader;
  private highlighter!: OBF.Highlighter;
  private hider!: OBC.Hider;
  private classifier!: OBC.Classifier;
  private tools!: ViewerTools;
  private container: HTMLDivElement;
  private dispatch: Dispatch<any>;
  private initialized: Promise<void>;
  private propertiesPanelState: PropertiesPanelState = {
    elementName: "",
    propertySets: {},
    searchQuery: "",
    statusMessage: "Select an element",
    loading: false,
  };
  private updatePropertiesPanel?: BUI.UpdateFunction<PropertiesPanelState>;
  private propertiesPanel?: HTMLElement;
  private propertiesPanelContainer?: HTMLElement;
  private layersTable!: BUI.Table<LayerRow>;
  private layersPanel?: HTMLElement;
  private layersPanelContainer?: HTMLElement;
  private layerVisibility = new Map<string, boolean>();
  private propertiesRequestId = 0;

  constructor(container: HTMLDivElement, dispatch: Dispatch<any>) {
    this.dispatch = dispatch;
    this.container = container;
    this.components = new OBC.Components();
    this.worlds = this.components.get(OBC.Worlds);

    const world = this.worlds.create<
      OBC.SimpleScene,
      OBC.OrthoPerspectiveCamera,
      OBF.PostproductionRenderer
    >();
    this.world = world;

    const sceneComponent = new OBC.SimpleScene(this.components);
    world.scene = sceneComponent;
    this.scene = world.scene.three;
    sceneComponent.setup();
    // The canvas colour (--CanvasSurface) painted by the scene, not left to
    // the page behind a transparent canvas: post-production drops the alpha of
    // see-through things, like a section plane, drawn over an empty background.
    this.scene.background = new THREE.Color("#33383a");

    const rendererComponent = new OBF.PostproductionRenderer(
      this.components,
      container,
    );
    world.renderer = rendererComponent;
    world.renderer.showLogo = false;

    const orthoCamera = new OBC.OrthoPerspectiveCamera(this.components);
    world.camera = orthoCamera;
    this.camera = orthoCamera;

    this.components.init();

    rendererComponent.postproduction.enabled = true;

    this.fragments = this.components.get(OBC.FragmentsManager);

    this.initialized = this.Init();
  }

  private async Init() {
    const world = this.world;
    const camera = this.camera;

    const workerUrl = await OBC.FragmentsManager.getWorker();
    this.fragments.init(workerUrl);

    camera.controls.addEventListener("update", () =>
      this.fragments.core.update(),
    );

    this.fragments.list.onItemSet.add(({ value: model }) => {
      model.useCamera(camera.three);
      this.scene.add(model.object);
      this.fragments.core.update(true);
    });

    this.fragments.core.models.materials.list.onItemSet.add(
      ({ value: material }) => {
        const isLod = "isLodMaterial" in material && material.isLodMaterial;
        if (!isLod) {
          material.polygonOffset = true;
          material.polygonOffsetUnits = 1;
          material.polygonOffsetFactor = Math.random();
        }
      },
    );

    this.hider = this.components.get(OBC.Hider);
    this.classifier = this.components.get(OBC.Classifier);
    this.ifcLoader = this.components.get(OBC.IfcLoader);

    this.components.get(OBC.Raycasters).get(world);

    this.highlighter = this.components.get(OBF.Highlighter);
    this.highlighter.setup({
      world,
      selectMaterialDefinition: {
        color: new THREE.Color("#bcf124"),
        opacity: 1,
        transparent: false,
        renderedFaces: 0,
      },
    });
    this.highlighter.zoomToSelection = true;

    this.highlighter.events.select.onHighlight.add((modelIdMap) => {
      void this.LoadPropertiesForSelection(modelIdMap);
    });
    this.highlighter.events.select.onClear.add(() => {
      void this.LoadPropertiesForSelection({});
    });

    this.layersTable = this.CreateLayersTable();

    this.tools = new ViewerTools(
      this.components,
      world,
      camera,
      this.highlighter,
      this.container,
    );
  }

  Dispose() {
    this.UnmountPropertiesPanel();
    this.UnmountLayersPanel();
    this.tools?.Dispose();
    if (this.components) {
      this.components.dispose();
    }
    (this.components as any) = undefined;
    if (this.fragments) {
      this.fragments.dispose();
      (this.fragments as any) = undefined;
    }
  }

  async MountPropertiesPanel(container: HTMLElement) {
    await this.initialized;

    if (this.propertiesPanel && this.propertiesPanelContainer === container) {
      return;
    }

    this.UnmountPropertiesPanel();

    const [propertiesPanel, updatePropertiesPanel] = BUI.Component.create<
      HTMLElement,
      PropertiesPanelState
    >((state) => this.RenderPropertiesPanel(state), this.propertiesPanelState);

    this.updatePropertiesPanel = updatePropertiesPanel;
    container.replaceChildren(propertiesPanel);
    this.propertiesPanel = propertiesPanel;
    this.propertiesPanelContainer = container;
    this.SyncPropertiesPanelWithSelection();
  }

  UnmountPropertiesPanel() {
    if (this.propertiesPanelContainer) {
      this.propertiesPanelContainer.replaceChildren();
    }
    this.propertiesPanel = undefined;
    this.propertiesPanelContainer = undefined;
    this.updatePropertiesPanel = undefined;
  }

  async MountLayersPanel(container: HTMLElement) {
    await this.initialized;

    if (this.layersPanel && this.layersPanelContainer === container) {
      return;
    }

    this.UnmountLayersPanel();

    const layersTable = this.layersTable;
    layersTable.preserveStructureOnFilter = true;
    const layersPanel = BUI.Component.create(() => {
      const onTextInput = (e: Event) => {
        const input = e.target as BUI.TextInput;
        layersTable.queryString = input.value !== "" ? input.value : null;
      };

      return BUI.html`
        <bim-panel label="Layers">
          <bim-panel-section
            label="Model Layers"
            icon="mdi:layers-triple"
            fixed
          >
            <bim-text-input
              @input=${onTextInput}
              placeholder="Search Layer"
              debounce="250"
            ></bim-text-input>
            ${layersTable}
          </bim-panel-section>
        </bim-panel>
      `;
    });

    container.replaceChildren(layersPanel);
    this.layersPanel = layersPanel;
    this.layersPanelContainer = container;
  }

  UnmountLayersPanel() {
    if (this.layersPanelContainer) {
      this.layersPanelContainer.replaceChildren();
    }
    this.layersPanel = undefined;
    this.layersPanelContainer = undefined;
  }

  private CreateLayersTable(): BUI.Table<LayerRow> {
    const table = document.createElement("bim-table") as BUI.Table<LayerRow>;
    table.headersHidden = true;
    table.columns = [
      { name: "Name", width: "1fr" },
      { name: "Actions", width: "auto", forceDataTransform: true },
    ];
    table.hiddenColumns = ["classification"];
    table.dataTransform = {
      Actions: (_value: BUI.TableCellValue, rowData: Partial<LayerRow>) => {
        const classification = rowData.classification;
        const group = rowData.Name;
        if (!classification || !group) return "";
        const onChange = async (e: Event) => {
          const checkbox = e.target as BUI.Checkbox;
          this.layerVisibility.set(group, checkbox.value);
          const groupData = this.classifier.list
            .get(classification)
            ?.get(group);
          if (!groupData) return;
          const map = await groupData.get();
          await this.hider.set(checkbox.value, map);
        };
        const isVisible = this.layerVisibility.get(group) ?? true;
        return BUI.html`<bim-checkbox ?checked=${isVisible} @change=${onChange}></bim-checkbox>`;
      },
    };
    return table;
  }

  private clearPresentationLayerGroups() {
    if (this.classifier.list.has(PRESENTATION_LAYERS_CLASSIFICATION)) {
      this.classifier.list.delete(PRESENTATION_LAYERS_CLASSIFICATION);
    }
  }

  private registerPresentationLayerGroups(
    modelId: string,
    layerToLocalIds: Map<string, number[]>,
  ) {
    for (const [layerName, localIds] of Array.from(layerToLocalIds)) {
      this.classifier.addGroupItems(PRESENTATION_LAYERS_CLASSIFICATION, layerName, {
        [modelId]: new Set(localIds),
      });
    }
  }

  private async ClassifyModel() {
    this.clearPresentationLayerGroups();

    try {
      for (const [modelId, model] of Array.from(this.fragments.list)) {
        invalidatePresentationLayersCache(model);
        const layerToLocalIds = await readPresentationLayersFromModel(model);
        this.registerPresentationLayerGroups(modelId, layerToLocalIds);
      }
    } catch (error) {
      console.error("Unable to load presentation layers:", error);
    }

    this.UpdateLayersTable();
  }

  private async PersistPresentationLayersIndex(
    modelId: string,
    model: FRAGS.FragmentsModel,
    layerToLocalIds: Map<string, number[]>,
  ) {
    if (!layerToLocalIds.size) return;

    const indexData = buildPresentationLayersIndexData(layerToLocalIds);
    const editor = this.fragments.core.editor;
    const indexNames = await model.getIndexNames();
    if (indexNames.includes(indexData.name)) {
      editor.deleteIndex(modelId, indexData.name);
    }
    editor.createIndex(modelId, indexData);
    await editor.applyChanges(modelId);
  }

  private async ApplyPresentationLayersFromExtracted(
    modelId: string,
    model: FRAGS.FragmentsModel,
    layers: Map<string, Set<number>>,
    expressToGuid: Map<number, string>,
  ): Promise<boolean> {
    const guidToLocal = await buildGuidToLocalIdMap(model);
    const layerToLocalIds = layerExpressMapToLocalIds(
      layers,
      expressToGuid,
      guidToLocal,
    );

    if (!layerToLocalIds.size) return false;

    await this.PersistPresentationLayersIndex(modelId, model, layerToLocalIds);
    return true;
  }

  /** Commits pending fragment edits (e.g. custom indexes) before export. */
  private async exportFragmentsBuffer(
    modelId: string,
    hasPendingEdits: boolean,
  ): Promise<ArrayBuffer> {
    if (hasPendingEdits) {
      await this.fragments.core.editor.save(modelId);
    }

    const model = this.fragments.list.get(modelId);
    if (!model) {
      throw new Error(`Model "${modelId}" not found for fragment export`);
    }

    return model.getBuffer(false);
  }

  private UpdateLayersTable() {
    const groups = this.classifier.list.get(PRESENTATION_LAYERS_CLASSIFICATION);
    const rows: BUI.TableGroupData<LayerRow>[] = [];
    const currentLayerNames = new Set<string>();

    if (groups) {
      for (const layerName of Array.from(groups.keys()).sort((a, b) =>
        a.localeCompare(b),
      )) {
        currentLayerNames.add(layerName);
        if (!this.layerVisibility.has(layerName)) {
          this.layerVisibility.set(layerName, true);
        }
        rows.push({
          data: {
            Name: layerName,
            classification: PRESENTATION_LAYERS_CLASSIFICATION,
            Actions: "",
          },
        });
      }
    }

    for (const layerName of Array.from(this.layerVisibility.keys())) {
      if (!currentLayerNames.has(layerName)) {
        this.layerVisibility.delete(layerName);
      }
    }

    this.layersTable.data = rows;
  }

  private refreshPropertyPanelSectionHeights() {
    const container = this.propertiesPanelContainer;
    if (!container) return;

    const panel = container.querySelector("bim-panel");
    if (!panel) return;

    const sections = Array.from(
      panel.querySelectorAll("bim-panel-section"),
    ) as BUI.PanelSection[];

    for (const section of sections) {
      section.style.height = "auto";
    }
  }

  private setPropertySetSectionsExpanded(expanded: boolean) {
    const container = this.propertiesPanelContainer;
    if (!container) return;

    const sections = container.querySelectorAll(
      "bim-panel-section:not([fixed])",
    );
    for (const section of Array.from(sections)) {
      const panelSection = section as unknown as {
        collapsed: boolean;
        animateHeader?: () => void;
      };
      panelSection.collapsed = !expanded;
      panelSection.animateHeader?.();
    }

    requestAnimationFrame(() => {
      this.refreshPropertyPanelSectionHeights();
    });
  }

  private RenderPropertiesPanel(state: PropertiesPanelState) {
    const onTextInput = (e: Event) => {
      const input = e.target as BUI.TextInput;
      this.UpdatePropertiesPanelState({ searchQuery: input.value });
    };

    const expandAll = () => {
      this.setPropertySetSectionsExpanded(true);
    };

    const collapseAll = () => {
      this.setPropertySetSectionsExpanded(false);
    };

    const filteredSets = filterPropertySets(
      state.propertySets,
      state.searchQuery,
    );
    const setNames = sortPropertySetNames(Object.keys(filteredSets));

    const psetSections = setNames.map((setName) => {
      const props = filteredSets[setName];
      const rows = Object.entries(props).map(([label, value]) => BUI.html`
        <div class="property-row">
          <div class="property-label">${label}</div>
          <div class="property-value">${String(value)}</div>
        </div>
      `);

      return BUI.html`
        <bim-panel-section label="${setName}" icon="solar:document-bold">
          ${rows}
        </bim-panel-section>
      `;
    });

    const headerLabel = state.elementName || "Properties";
    const statusBlock = state.loading
      ? BUI.html`<bim-label class="property-status">Loading properties…</bim-label>`
      : state.statusMessage
        ? BUI.html`<bim-label class="property-status">${state.statusMessage}</bim-label>`
        : setNames.length === 0
          ? BUI.html`<bim-label class="property-status">No properties found</bim-label>`
          : null;

    return BUI.html`
      <bim-panel label="Properties">
        <bim-panel-section fixed label="${headerLabel}">
            <bim-text-input
              @input=${onTextInput}
              placeholder="Search Property"
              debounce="250"
              value=${state.searchQuery}
            ></bim-text-input>
            <div style="display: flex; gap: 0.5rem; flex-wrap: wrap;">
              <bim-button @click=${expandAll} label="Expand All"></bim-button>
              <bim-button @click=${collapseAll} label="Collapse All"></bim-button>
            </div>
            ${statusBlock}
        </bim-panel-section>
        ${psetSections}
      </bim-panel>
    `;
  }

  private UpdatePropertiesPanelState(partial: Partial<PropertiesPanelState>) {
    Object.assign(this.propertiesPanelState, partial);
    this.updatePropertiesPanel?.({ ...this.propertiesPanelState });

    if (partial.loading === false) {
      requestAnimationFrame(() => {
        this.refreshPropertyPanelSectionHeights();
      });
    }
  }

  private async LoadPropertiesForSelection(modelIdMap: OBC.ModelIdMap) {
    const requestId = ++this.propertiesRequestId;
    const isStale = () => requestId !== this.propertiesRequestId;
    const selectionCount = countSelectedElements(modelIdMap);

    if (selectionCount === 0) {
      this.UpdatePropertiesPanelState({
        elementName: "",
        propertySets: {},
        statusMessage: "Select an element",
        loading: false,
      });
      return;
    }

    const selection = getFirstSelectedElement(modelIdMap);
    if (!selection) {
      this.UpdatePropertiesPanelState({
        elementName: "",
        propertySets: {},
        statusMessage: "Select an element",
        loading: false,
      });
      return;
    }

    const { modelId, localId } = selection;
    const model = this.fragments.list.get(modelId);
    if (!model) {
      this.UpdatePropertiesPanelState({
        elementName: "",
        propertySets: {},
        statusMessage: "Model not found",
        loading: false,
      });
      return;
    }

    this.UpdatePropertiesPanelState({
      loading: true,
      statusMessage: null,
    });

    try {
      const [elementName, propertySets] = await Promise.all([
        getElementName(model, localId),
        getElementPropertySets(model, localId),
      ]);

      if (isStale()) return;

      const multiSelectNote =
        selectionCount > 1 ? ` (${selectionCount} elements selected)` : "";

      this.UpdatePropertiesPanelState({
        elementName: `${elementName}${multiSelectNote}`,
        propertySets,
        statusMessage:
          Object.keys(propertySets).length === 0
            ? "No property sets found"
            : null,
        loading: false,
      });
    } catch (error) {
      if (isStale()) return;
      console.error("Unable to load element properties:", error);
      this.UpdatePropertiesPanelState({
        elementName: "",
        propertySets: {},
        statusMessage: "Unable to load properties",
        loading: false,
      });
    }
  }

  SyncPropertiesPanelWithSelection() {
    if (!this.highlighter) return;
    void this.LoadPropertiesForSelection(
      this.highlighter.selection.select ?? {},
    );
  }

  async HideModelElement() {
    await this.initialized;
    const selection = this.highlighter.selection.select ?? {};
    if (OBC.ModelIdMapUtils.isEmpty(selection)) return;
    await this.hider.set(false, selection);
  }

  async IsolateModelElement() {
    await this.initialized;
    const selection = this.highlighter.selection.select ?? {};
    if (OBC.ModelIdMapUtils.isEmpty(selection)) return;
    await this.hider.isolate(selection);
  }

  async UnhideAllModelElements() {
    await this.initialized;
    await this.hider.set(true);
  }

  ShowModelElementProperties() {
    this.SyncPropertiesPanelWithSelection();
  }

  async SetActiveTool(tool: ViewerTool) {
    await this.initialized;
    this.tools.SetActiveTool(tool);
  }

  async SetMeasureUnits(units: MeasureUnits) {
    await this.initialized;
    this.tools.SetMeasureUnits(units);
  }

  async ClearMeasurements() {
    await this.initialized;
    this.tools.ClearMeasurements();
  }

  async ClearSections() {
    await this.initialized;
    this.tools.ClearSections();
  }

  async FitModel() {
    await this.initialized;
    await this.tools.FitModel();
  }

  async SetProjection(projection: ViewerProjection) {
    await this.initialized;
    await this.tools.SetProjection(projection);
  }

  async SetNavigation(navigation: ViewerNavigation) {
    await this.initialized;
    await this.tools.SetNavigation(navigation);
  }

  async MountModelTree(container: HTMLElement) {
    await this.initialized;
    await this.tools.MountModelTree(container);
  }

  UnmountModelTree() {
    this.tools?.UnmountModelTree();
  }

  async ConvertIfcToFragments(ifcModel: any) {
    try {
      await this.initialized;

      await this.ifcLoader.setup({
        autoSetWasm: false,
        wasm: IFC_WASM,
      });

      const ifcFile = ifcModel["modelFileData"] as File;
      const buffer = new Uint8Array(await ifcFile.arrayBuffer());
      const modelId = ifcModel["modelName"] as string;

      this.clearPresentationLayerGroups();

      const ifcModelId = await this.ifcLoader.readIfcFile(buffer);
      const { layers, expressToGuid } = await extractPresentationLayersFromIfc(
        this.ifcLoader.webIfc,
        ifcModelId,
      );
      try {
        this.ifcLoader.webIfc.CloseModel(ifcModelId);
      } catch {
        /* web-ifc model may already be closed */
      }

      const model = await this.ifcLoader.load(buffer, false, modelId);
      const hasLayerIndex = await this.ApplyPresentationLayersFromExtracted(
        modelId,
        model,
        layers,
        expressToGuid,
      );

      const fragsBuffer = await this.exportFragmentsBuffer(
        modelId,
        hasLayerIndex,
      );

      if (hasLayerIndex) {
        await this.ClassifyModel();
      } else {
        this.UpdateLayersTable();
      }
      return {
        modelName: ifcModel["modelName"],
        modelFragmentsData: fragsBuffer,
        modelFragmentsSize: fragsBuffer.byteLength,
      };
    } catch (error: any) {
      console.error(`An error occurred: ${error.message}`);
    }
  }

  async OpenModelFragments(payload: ModelFragmentsPayload): Promise<boolean> {
    try {
      await this.initialized;

      const fragmentsResponse = await fetch(payload.modelFragmentsUrl);

      if (!fragmentsResponse.ok) {
        throw new Error(
          `Failed to download model fragments: ${fragmentsResponse.status}`,
        );
      }

      const buffer = new Uint8Array(await fragmentsResponse.arrayBuffer());
      const modelId = payload.modelName ?? `model-${Date.now()}`;

      if (this.fragments.list.has(modelId)) {
        await this.fragments.core.disposeModel(modelId);
      }

      await this.fragments.core.load(buffer, { modelId });

      await this.ClassifyModel();
      return true;
    } catch (error) {
      console.error("Error opening model fragments:", error);
      return false;
    }
  }
}
